import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAudio } from 'react-use';
import { useMediaQuery } from '@mui/material';

const steps = ['visual', 'auditory', 'calculation', 'stroop', 'shape', 'color'];

interface ClientInstructionProps {
  setOpen: Function;
}

const soundImgStyle = {
  cursor: 'pointer',
};

const inlineImage = { display: 'inline', height: '2.2em', marginLeft: '0.5em' };

const SINGLE_SOUND_URL = 'assets/tutorial/auditory_single.mp3';
const DOUBLE_SOUND_URL = 'assets/tutorial/auditory_double.mp3';

const ClientInstructions: React.FC<ClientInstructionProps> = ({ setOpen }) => {
  const { t, i18n } = useTranslation();
  const isTablet = useMediaQuery(
    '(pointer: coarse) and (max-width: 1400px), (pointer: coarse) and (orientation: landscape)'
  );

  const [audio, state, controls] = useAudio({
    src: SINGLE_SOUND_URL,
  });

  const [doubleAudio, dState, dControls] = useAudio({
    src: DOUBLE_SOUND_URL,
  });
  const [currentStep, setCurrentStep] = useState(steps[0]);

  const nextIndex = useMemo(() => {
    const foundIndex = steps.findIndex(item => item === currentStep);
    if (foundIndex !== -1 && foundIndex < steps.length - 1) {
      return foundIndex + 1;
    }
    return null;
  }, [currentStep]);

  const handleClickNext = () => {
    if (nextIndex) {
      setCurrentStep(steps[nextIndex]);
    } else {
      setCurrentStep(steps[0]);
      setOpen(false);
    }
  };

  const CalculationTask = useMemo(() => {
    if (currentStep !== steps[2]) {
      return null;
    }
    return (
      <div className="instruction-row">
        <h4 className="text-xl font-semibold mb-4">{t('calculation-task-instruction')}</h4>
        <div className="instruction-row">
          <p className="inline-text">
            {t(!isTablet ? 'calculation-task-description' : 'calculation-task-description-tablet')}
            {isTablet && <img style={inlineImage} src={'assets/tutorial/CALC.png'} />}
          </p>
          <div className="calculation-container">
            <p>{t('calculation-task-example')}</p>
          </div>
        </div>
        <div className="keyboard-container center">
          {!isTablet && <img alt={'keyboard-down'} src={'assets/tutorial/keyboard_arrowup_highlight.png'} />}
        </div>
      </div>
    );
  }, [currentStep, controls, i18n, t]);

  const Color = useMemo(() => {
    if (currentStep !== steps[5]) {
      return null;
    }
    return (
      <div className="calculation-container font-mono">
        <h4 className="text-xl font-semibold mb-4">{t('color-task')}</h4>
        <div className="instruction-row">
          <p>{t('moving-ball-description')}</p>
          <div className="center">
            <img src={'assets/tutorial/EMDR-remote_sphere.png'} height="50px" alt={''} />
          </div>
        </div>
        <div className="instruction-row">
          <p>{t('finish')}</p>
          <p>{t('finish-description')}</p>
        </div>
      </div>
    );
  }, [currentStep, i18n, t]);

  const AudioTask = useMemo(() => {
    if (currentStep !== steps[1]) {
      return null;
    }
    return (
      <div className="instruction-row">
        <h4 className="text-xl font-semibold mb-4">{t('auditory-task')}</h4>
        <p>
          {t('begin-sound')}
          <a onClick={() => controls.play()}>{t('this-sound')}</a>
          {t('end-sound')}
        </p>
        <div className="instruction-row sub-row">
          {!isTablet ? (
            <>
              <div className="sub-row-container">
                <img
                  alt={'push-down'}
                  style={soundImgStyle}
                  src={'assets/tutorial/cue_sound_once.png'}
                  onClick={() => controls.play()}
                />
                <p>{t('1-push-arrow')}</p>
              </div>
              <div className="sub-row-container">
                <img
                  alt={'push-down'}
                  style={soundImgStyle}
                  src={'assets/tutorial/cue_sound_double.png'}
                  onClick={() => dControls.play()}
                />
                <p>{t('2-push-arrow')}</p>
              </div>
            </>
          ) : (
            <>
              <div className="sub-row-container">
                <img
                  alt={'push-down'}
                  style={soundImgStyle}
                  src={'assets/tutorial/cue_sound_once.png'}
                  onClick={() => controls.play()}
                />
                <p>{t('1-push-shift')}</p>
                <img style={{ margin: 12 }} alt={''} src={'assets/tutorial/AUDITORY.png'} />
              </div>
              <div className="sub-row-container">
                <img
                  alt={'push-down'}
                  style={soundImgStyle}
                  src={'assets/tutorial/cue_sound_double.png'}
                  onClick={() => dControls.play()}
                />
                <p>{t('2-push-shift')}</p>
                <img style={{ margin: 12 }} alt={''} src={'assets/tutorial/AUDITORY.png'} />
              </div>
            </>
          )}
        </div>
        <div className="keyboard-container center">
          {!isTablet && <img alt={'keyboard-down'} src={'assets/tutorial/SHIFT.png'} />}
        </div>
      </div>
    );
  }, [currentStep, controls, i18n, t]);

  const VisualTask = useMemo(() => {
    if (currentStep !== steps[0]) {
      return null;
    }
    return (
      <div className="instruction-row font-mono">
        <h4 className="text-xl font-semibold mb-4">{t('reaction-task')}</h4>
        <p>{t('reaction-description')}</p>
        <div className="instruction-row sub-row">
          {!isTablet ? (
            <>
              <div className="sub-row-container">
                <img alt={''} src={'assets/tutorial/click_100.png'} />
                <p>{t('1-push-spacebar')}</p>
              </div>
              <div className="sub-row-container">
                <img alt={''} src={'assets/tutorial/doubleclick_100.png'} />
                <p>{t('2-push-spacebar')}</p>
              </div>
            </>
          ) : (
            <>
              <div className="sub-row-container">
                <img alt={''} src={'assets/tutorial/click_100.png'} />
                <p>{t('1-push-visual-tablet')}</p>
                <img style={{ margin: 12 }} alt={''} src={'assets/tutorial/VISUAL.png'} />
              </div>
              <div className="sub-row-container">
                <img alt={''} src={'assets/tutorial/doubleclick_100.png'} />
                <p>{t('2-push-visual-tablet')}</p>
                <img style={{ margin: 12 }} alt={''} src={'assets/tutorial/VISUAL.png'} />
              </div>
            </>
          )}
          <div className="sub-row-container">
            <img alt={''} src={'assets/tutorial/noclick_100.png'} />
            <p>{t('do-nothing')}</p>
          </div>
        </div>
        <div className="keyboard-container center">
          {!isTablet && <img alt={''} src={'assets/tutorial/keyboard_spacebar_highlight.png'} />}
        </div>
      </div>
    );
  }, [currentStep, i18n, t]);

  const Stroop = useMemo(() => {
    if (currentStep !== steps[3]) {
      return null;
    }
    return (
      <div className="instruction-row font-mono">
        <h4 className="text-xl font-semibold mb-4">{t('stroop-task-instruction')}</h4>
        <p>
          {t(!isTablet ? 'stroop-description' : 'stroop-description-tablet')}
          {isTablet && <img style={inlineImage} src={'assets/tutorial/STROOP.png'} />}
        </p>
        <div className="keyboard-container center">
          {!isTablet && <img alt={''} src={'assets/tutorial/LEFT.png'} />}
        </div>
      </div>
    );
  }, [currentStep, i18n, t]);

  const Shape = useMemo(() => {
    if (currentStep !== steps[4]) {
      return null;
    }
    return (
      <div className="instruction-row font-mono">
        <h4 className="text-xl font-semibold mb-4">{t('shape-task-instruction')}</h4>
        <p>
          {t(!isTablet ? 'shape-description' : 'shape-description-tablet')}
          {isTablet && <img style={inlineImage} src={'assets/tutorial/SHAPE.png'} />}
        </p>
        <div className="keyboard-container center">
          {!isTablet && <img alt={''} src={'assets/tutorial/RIGHT.png'} />}
        </div>
      </div>
    );
  }, [currentStep, i18n, t]);

  return (
    <div className="instructions">
      {audio}
      {doubleAudio}
      {VisualTask}
      {AudioTask}
      {CalculationTask}
      {Stroop}
      {Shape}
      {Color}
      <div className="button-container">
        <button
          className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
          onClick={handleClickNext}
        >
          {nextIndex ? t('next') : t('close')}
        </button>
      </div>
    </div>
  );
};

export default ClientInstructions;
